import { Link } from 'react-router-dom';
import { ArrowRight, Heart, ChevronRight } from 'lucide-react';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { useDocumentHead } from '@/hooks/useDocumentHead';
import { BreadcrumbSchema } from '@/components/seo/BreadcrumbSchema';
import type { OccasionConfig } from '@/data/occasions';
import { buildOccasionCtaHref } from '@/data/occasions';

interface Props {
  occasion: OccasionConfig;
}

export function OccasionPageLayout({ occasion }: Props) {
  useDocumentHead({
    title: occasion.metaTitle,
    description: occasion.metaDescription,
    path: occasion.path,
  });

  const ctaHref = buildOccasionCtaHref(occasion);

  // Single FAQPage JSON-LD block — respects SEO structured data memory.
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: occasion.faqs.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />

      <BreadcrumbSchema
        items={[
          { name: 'Home', path: '/' },
          { name: occasion.breadcrumb, path: occasion.path },
        ]}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />

      <main className="flex-1">
        {/* Breadcrumb */}
        <nav className="container max-w-4xl pt-6 text-xs text-muted-foreground" aria-label="Breadcrumb">
          <Link to="/" className="hover:text-foreground transition-colors">Home</Link>
          <ChevronRight className="inline w-3 h-3 mx-1 opacity-50" />
          <span className="text-foreground">{occasion.breadcrumb}</span>
        </nav>

        {/* Hero */}
        <section className="container max-w-3xl pt-10 pb-16 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium uppercase tracking-wider mb-6">
            <Heart className="w-3 h-3" />
            {occasion.eyebrow}
          </div>
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-serif leading-tight mb-6 text-foreground">
            {occasion.h1}
          </h1>
          <p className="text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            {occasion.subhead}
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-3 items-center justify-center">
            <Link
              to={ctaHref}
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-primary text-primary-foreground font-medium shadow-lg hover:shadow-xl transition-all hover:scale-[1.02]"
            >
              {occasion.ctaPrimary}
              <ArrowRight className="w-4 h-4" />
            </Link>
            {occasion.ctaSecondary && (
              <Link
                to="/living-announcements"
                className="text-sm text-muted-foreground hover:text-foreground transition-colors px-4 py-2"
              >
                {occasion.ctaSecondary} →
              </Link>
            )}
          </div>
          <p className="mt-4 text-xs text-muted-foreground">
            Your first 3 are free · No credit card required
          </p>
        </section>

        {/* Use case */}
        <section className="container max-w-2xl py-10">
          <p className="text-lg leading-relaxed text-foreground/90 text-center italic font-serif">
            "{occasion.useCase}"
          </p>
        </section>

        {/* Example announcement preview */}
        <section className="container max-w-2xl py-12">
          <p className="text-xs uppercase tracking-widest text-muted-foreground text-center mb-6">
            A real example
          </p>
          <article className="relative rounded-2xl border border-primary/20 bg-card/50 backdrop-blur p-8 sm:p-12 shadow-2xl">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-primary text-primary-foreground text-xs font-medium">
              Living Announcement
            </div>
            <h2 className="text-2xl sm:text-3xl font-serif text-center mb-6 text-foreground">
              {occasion.example.title}
            </h2>
            <p className="text-base sm:text-lg leading-relaxed text-foreground/90 text-center font-serif italic mb-6">
              {occasion.example.body}
            </p>
            <p className="text-sm text-muted-foreground text-center">
              — {occasion.example.signoff}
            </p>
          </article>
        </section>

        {/* Mid CTA */}
        <section className="container max-w-xl py-8 text-center">
          <Link
            to={ctaHref}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary text-primary-foreground font-medium hover:shadow-lg transition-all"
          >
            {occasion.ctaPrimary}
            <ArrowRight className="w-4 h-4" />
          </Link>
        </section>

        {/* FAQ */}
        <section className="container max-w-2xl py-16">
          <h2 className="text-2xl sm:text-3xl font-serif text-center mb-10 text-foreground">
            Questions people ask
          </h2>
          <div className="space-y-6">
            {occasion.faqs.map((f, i) => (
              <details
                key={i}
                className="group rounded-xl border border-border bg-card/50 p-5 transition-colors hover:bg-card"
              >
                <summary className="cursor-pointer list-none flex items-center justify-between gap-3 font-medium text-foreground">
                  <span>{f.q}</span>
                  <ChevronRight className="w-4 h-4 shrink-0 transition-transform group-open:rotate-90 opacity-60" />
                </summary>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        {/* Final CTA */}
        <section className="container max-w-2xl py-16 text-center">
          <p className="text-sm text-muted-foreground uppercase tracking-widest mb-4">
            Don't wait for the eulogy
          </p>
          <h2 className="text-3xl sm:text-4xl font-serif mb-6 text-foreground">
            Say it now — while they can hear you.
          </h2>
          <Link
            to={ctaHref}
            className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-primary text-primary-foreground font-medium text-base shadow-lg hover:shadow-xl transition-all hover:scale-[1.02]"
          >
            {occasion.ctaPrimary}
            <ArrowRight className="w-4 h-4" />
          </Link>
        </section>
      </main>

      <Footer />
    </div>
  );
}
